import { useState, useEffect } from "react"
import { Search, Plus, Star, BookOpen, ArrowLeft, MoreHorizontal, SquarePen, Trash, Users, Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { CategoryCombobox } from "@/components/ui/category-combobox"
import { usePromptStore } from "@/stores/prompt-store"
import type { PromptTemplate } from "@/lib/api"
import { cn } from "@/lib/utils"

interface PromptLibraryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectPrompt: (promptContent: string) => void
}

type DialogMode = 'browse' | 'create' | 'edit'
type VisibilityTab = 'all' | 'personal' | 'shared'

export function PromptLibraryDialog({ open, onOpenChange, onSelectPrompt }: PromptLibraryDialogProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [mode, setMode] = useState<DialogMode>('browse')
  const [editingPrompt, setEditingPrompt] = useState<PromptTemplate | null>(null)
  const [deletePromptId, setDeletePromptId] = useState<string | null>(null)

  // Form state
  const [formTitle, setFormTitle] = useState("")
  const [formContent, setFormContent] = useState("")
  const [formIsPinned, setFormIsPinned] = useState(false)
  const [formCategoryId, setFormCategoryId] = useState<string | null>(null)
  const [formVisibility, setFormVisibility] = useState<'personal' | 'shared'>('personal')
  const [isSaving, setIsSaving] = useState(false)

  // Store
  const {
    categories,
    isLoading,
    visibilityFilter,
    selectedCategoryId,
    fetchPrompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
    createCategory,
    setVisibilityFilter,
    setSelectedCategoryId,
    getFilteredPrompts,
  } = usePromptStore()

  const filteredPrompts = getFilteredPrompts(searchQuery)
  const pinnedPrompts = filteredPrompts.filter((p) => p.isPinned)
  const unpinnedPrompts = filteredPrompts.filter((p) => !p.isPinned)

  // Fetch prompts when dialog opens
  useEffect(() => {
    if (open) {
      void fetchPrompts()
    }
  }, [open, fetchPrompts])

  const resetForm = () => {
    setFormTitle("")
    setFormContent("")
    setFormIsPinned(false)
    setFormCategoryId(null)
    setFormVisibility('personal')
    setEditingPrompt(null)
  }

  const handleCreateNew = () => {
    resetForm()
    setMode('create')
  }

  const handleEdit = (prompt: PromptTemplate) => {
    setEditingPrompt(prompt)
    setFormTitle(prompt.title)
    setFormContent(prompt.content)
    setFormIsPinned(prompt.isPinned)
    setFormCategoryId(prompt.categoryId)
    setFormVisibility(prompt.visibility)
    setMode('edit')
  }

  const handleCancelForm = () => {
    resetForm()
    setMode('browse')
  }

  const handleSave = async () => {
    if (!formTitle.trim() || !formContent.trim() || isSaving) return

    setIsSaving(true)
    try {
      if (mode === 'edit' && editingPrompt) {
        await updatePrompt(editingPrompt.id, {
          title: formTitle.trim(),
          content: formContent.trim(),
          isPinned: formIsPinned,
          categoryId: formCategoryId,
          visibility: formVisibility,
        })
      } else {
        await createPrompt({
          title: formTitle.trim(),
          content: formContent.trim(),
          isPinned: formIsPinned,
          categoryId: formCategoryId,
          visibility: formVisibility,
        })
      }

      resetForm()
      setMode('browse')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = (id: string) => {
    setDeletePromptId(id)
  }

  const confirmDelete = async () => {
    if (deletePromptId) {
      await deletePrompt(deletePromptId)
      setDeletePromptId(null)
    }
  }

  // Reset state when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm()
      setMode('browse')
      setSearchQuery("")
    }
    onOpenChange(open)
  }

  const renderPromptCard = (prompt: PromptTemplate) => (
    <div
      key={prompt.id}
      className="group relative w-full text-left p-3 rounded-lg border border-border hover:bg-accent transition-colors"
    >
      <button
        className="w-full text-left"
        onClick={() => onSelectPrompt(prompt.content)}
      >
        <div className="flex items-center gap-2 mb-1 pr-8">
          {prompt.isPinned && (
            <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />
          )}
          <span className="font-medium text-sm truncate">{prompt.title}</span>
          {prompt.categoryName && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">
              {prompt.categoryName}
            </Badge>
          )}
          {prompt.visibility === 'shared' && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">
              Shared
            </Badge>
          )}
        </div>
        <p className="text-meta line-clamp-2">
          {prompt.content}
        </p>
        {prompt.visibility === 'shared' && prompt.creatorName && (
          <p className="text-[11px] text-muted-foreground mt-1">
            by {prompt.creatorName}
          </p>
        )}
      </button>

      {/* Actions dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleEdit(prompt)}>
            <SquarePen className="h-4 w-4 mr-2" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => handleDelete(prompt.id)}
          >
            <Trash className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {mode !== 'browse' ? (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCancelForm}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  {mode === 'edit' ? 'Edit Prompt' : 'Create New Prompt'}
                </div>
              ) : (
                "Prompt Library"
              )}
            </DialogTitle>
          </DialogHeader>

          {mode !== 'browse' ? (
            // Create/Edit Mode
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="prompt-form-title">Title</Label>
                <Input
                  id="prompt-form-title"
                  placeholder="e.g., Technical Requirements Extract"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="prompt-form-content">Prompt</Label>
                <Textarea
                  id="prompt-form-content"
                  placeholder="Enter your prompt..."
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  className="min-h-[120px]"
                />
              </div>

              <div className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <Label>Category</Label>
                  <CategoryCombobox
                    categories={categories}
                    value={formCategoryId}
                    onChange={setFormCategoryId}
                    onCreateCategory={createCategory}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Visibility</Label>
                  <div className="flex rounded-lg border border-border overflow-hidden h-9">
                    <button
                      type="button"
                      className={cn(
                        "px-3 text-xs transition-colors",
                        formVisibility === 'personal'
                          ? "bg-foreground text-background font-medium"
                          : "hover:bg-accent text-muted-foreground"
                      )}
                      onClick={() => setFormVisibility('personal')}
                    >
                      Personal
                    </button>
                    <button
                      type="button"
                      className={cn(
                        "px-3 text-xs transition-colors flex items-center gap-1",
                        formVisibility === 'shared'
                          ? "bg-foreground text-background font-medium"
                          : "hover:bg-accent text-muted-foreground"
                      )}
                      onClick={() => setFormVisibility('shared')}
                    >
                      <Users className="h-3 w-3" />
                      Shared
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="prompt-form-pin"
                  checked={formIsPinned}
                  onCheckedChange={(checked) => setFormIsPinned(checked === true)}
                />
                <Label htmlFor="prompt-form-pin" className="flex items-center gap-1.5 cursor-pointer">
                  <Star className="h-3.5 w-3.5" />
                  Add to favorites
                </Label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={handleCancelForm}>
                  Cancel
                </Button>
                <Button
                  onClick={() => void handleSave()}
                  disabled={!formTitle.trim() || !formContent.trim() || isSaving}
                >
                  {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  {mode === 'edit' ? 'Save Changes' : 'Save Prompt'}
                </Button>
              </div>
            </div>
          ) : (
            // Browse Mode
            <div className="space-y-3">
              {/* Visibility Tabs */}
              <div className="flex gap-0 border-b border-border">
                {(['all', 'personal', 'shared'] as VisibilityTab[]).map((tab) => (
                  <button
                    key={tab}
                    className={cn(
                      "px-4 py-2 text-sm transition-colors border-b-2",
                      visibilityFilter === tab
                        ? "border-foreground text-foreground font-medium"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    )}
                    onClick={() => setVisibilityFilter(tab)}
                  >
                    {tab === 'all' ? 'All' : tab === 'personal' ? 'My Prompts' : 'Shared'}
                  </button>
                ))}
              </div>

              {/* Search and Create */}
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleCreateNew}>
                  <Plus className="h-4 w-4 mr-1" />
                  Create New
                </Button>
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search prompts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
              </div>

              {/* Category Filter Chips */}
              {categories.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  <button
                    className={cn(
                      "px-2.5 py-1 rounded-lg text-xs transition-colors border",
                      !selectedCategoryId
                        ? "bg-foreground text-background border-foreground"
                        : "bg-transparent text-muted-foreground border-border hover:bg-accent"
                    )}
                    onClick={() => setSelectedCategoryId(null)}
                  >
                    All
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      className={cn(
                        "px-2.5 py-1 rounded-lg text-xs transition-colors border",
                        selectedCategoryId === category.id
                          ? "bg-foreground text-background border-foreground"
                          : "bg-transparent text-muted-foreground border-border hover:bg-accent"
                      )}
                      onClick={() => setSelectedCategoryId(category.id)}
                    >
                      {category.name}
                    </button>
                  ))}
                  <button
                    className={cn(
                      "px-2.5 py-1 rounded-lg text-xs transition-colors border",
                      selectedCategoryId === 'uncategorized'
                        ? "bg-foreground text-background border-foreground"
                        : "bg-transparent text-muted-foreground border-border hover:bg-accent"
                    )}
                    onClick={() => setSelectedCategoryId('uncategorized')}
                  >
                    Uncategorized
                  </button>
                </div>
              )}

              {/* Prompt List */}
              <ScrollArea className="h-[400px]">
                {isLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="p-3 rounded-lg border border-border">
                        <Skeleton className="h-4 w-48 mb-2" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-3/4 mt-1" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Pinned Section */}
                    {pinnedPrompts.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-1.5 px-1 py-1.5 text-xs font-medium text-muted-foreground">
                          <Star className="h-3.5 w-3.5 fill-current" />
                          Pinned
                        </div>
                        <div className="space-y-2">
                          {pinnedPrompts.map((prompt) => renderPromptCard(prompt))}
                        </div>
                      </div>
                    )}

                    {/* All Prompts Section */}
                    {unpinnedPrompts.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1.5 px-1 py-1.5 text-xs font-medium text-muted-foreground">
                          <BookOpen className="h-3.5 w-3.5" />
                          All Prompts
                        </div>
                        <div className="space-y-2">
                          {unpinnedPrompts.map((prompt) => renderPromptCard(prompt))}
                        </div>
                      </div>
                    )}

                    {filteredPrompts.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <BookOpen className="h-8 w-8 text-muted-foreground/50 mb-2" />
                        <p className="text-sm text-muted-foreground">No prompts found</p>
                      </div>
                    )}
                  </>
                )}
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletePromptId} onOpenChange={(open) => !open && setDeletePromptId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prompt</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this prompt? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void confirmDelete()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
